/* eslint-disable import/no-unused-modules */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getWizardConfiguration, getInitialFormData, transformModelToFormData } from '../config/wizardConfig';
import { DEFAULT_WIZARD_CONFIG, WIZARD_MODES, WIZARD_STEP_KEYS } from '../constants';

export const useWizardFlow = ({ mode, initialData = {}, model, actions, translate = (key) => key }) => {
    const wizardConfig = useMemo(() => {
        const config = getWizardConfiguration(mode, translate);
        return config || DEFAULT_WIZARD_CONFIG;
    }, [mode, translate]);

    // Local state for step navigation when Redux is not available
    const [localCurrentStepIndex, setLocalCurrentStepIndex] = useState(0);

    // Get current step index from local state first, then Redux model
    const currentStepIndex = useMemo(() => {
        // Prioritize local state for reliable step navigation
        if (localCurrentStepIndex !== null && localCurrentStepIndex !== undefined) {
            return localCurrentStepIndex;
        }
        // Fallback to Redux state
        if (model?.activeStep && wizardConfig?.steps) {
            const index = wizardConfig.steps.findIndex(step => step.id === model.activeStep);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [localCurrentStepIndex, model?.activeStep, wizardConfig?.steps]);

    const [formData, setFormData] = useState(() => {
        // Transform model data to form data for both edit and create modes when model has data
        if (model && (mode === WIZARD_MODES.EDIT || model.powertrainName || model.selectedSite || model.selectedOrganization || model.addedAssets?.length > 0)) {
            return transformModelToFormData(model);
        }

        // Initialize form data structure based on config
        const initData = getInitialFormData();
        if (wizardConfig?.steps) {
            wizardConfig.steps.forEach(step => {
                if (initialData[step.key]) {
                    initData[step.key] = { ...initData[step.key], ...initialData[step.key] };
                }
            });
        }
        return initData;
    });

    const currentStep = wizardConfig?.steps?.[currentStepIndex] || null;

    // Load existing data for edit mode (with stable reference to prevent infinite loops)
    useEffect(() => {
        if (mode === WIZARD_MODES.EDIT && model?.powertrainID) {
            // Transform existing model data to form data structure
            const editData = {
                basicInfo: {
                    powertrainName: model?.powertrainName || '',
                    description: model?.description || '',
                    selectedSite: model?.selectedSite || null,
                    selectedLocation: model?.selectedLocation || null,
                    selectedOrganization: model?.selectedOrganization || null,
                    powertrainId: model?.powertrainID,
                    id: model?.powertrainID
                },
                assetSelection: {
                    selectedAssets: model?.addedAssets || model?.selectedAssets || [],
                    addedAssets: model?.addedAssets || [],
                    availableAssets: model?.availableAssets || [],
                    selectedAssetTypeAvailableSection: null,
                    assetFilterAvailableSection: '',
                    selectedAssetTypeAddedSection: null,
                    assetFilterAddedSection: ''
                },
                physicalConnection: {
                    selectedAssets: model?.addedAssets || model?.selectedAssets || [],
                    physicalConnection: model?.selectedPowertrain?.connectedAssets || [],
                    connections: model?.connectedAssets || [],
                    outputRatios: model?.outputRatios || {},
                    isSkipped: !(model?.connectedAssets?.length > 0)
                },
                review: {
                    agreesToTerms: false,
                    finalReviewCompleted: false
                },
                metadata: {
                    powertrainId: model?.powertrainID,
                    rowVersion: model?.rowVersion,
                    createdDate: model?.createdDate,
                    lastModified: model?.lastModified
                }
            };

            setFormData(prevData => {
                // Only update if data has actually changed (prevents infinite loops)
                if (prevData?.metadata?.powertrainId !== model?.powertrainID ||
                    prevData?.metadata?.rowVersion !== model?.rowVersion) {
                    return editData;
                }
                return prevData;
            });
        }
    }, [mode, model?.powertrainID, model?.rowVersion]);

    const canGoNext = useCallback(() => {
        if (!currentStep) { return false; }
        if (currentStep.isRequired && !formData[currentStep.key]) { return false; }
        const stepsLength = wizardConfig?.steps?.length || 0;
        return currentStepIndex < stepsLength - 1 || currentStepIndex === stepsLength - 1;
    }, [currentStep, currentStepIndex, formData, wizardConfig?.steps?.length]);

    const canGoPrevious = useCallback(() => {
        return currentStepIndex > 0;
    }, [currentStepIndex]);

    // Memoize the boolean values to prevent re-renders
    const canGoNextValue = useMemo(() => canGoNext(), [canGoNext]);
    const canGoPreviousValue = useMemo(() => canGoPrevious(), [canGoPrevious]);

    const isLastStep = currentStepIndex === (wizardConfig?.steps?.length || 1) - 1;
    const isFirstStep = currentStepIndex === 0;

    const goToStep = useCallback((stepIndex) => {
        const stepsLength = wizardConfig?.steps?.length || 0;
        if (stepIndex >= 0 && stepIndex < stepsLength) {
            // Use local state for reliable step navigation
            setLocalCurrentStepIndex(stepIndex);

            // Also update Redux state if available
            if (actions?.setWizardActiveStep && wizardConfig?.steps?.[stepIndex]) {
                actions.setWizardActiveStep(wizardConfig.steps[stepIndex].id);
            }

            // Save progress if enabled
            if (wizardConfig?.navigation?.saveProgressOnStep) {
                // Save current progress
            }
        }
    }, [wizardConfig?.steps, actions, wizardConfig?.navigation?.saveProgressOnStep]); const nextStep = useCallback(() => {
        if (canGoNextValue) {
            goToStep(currentStepIndex + 1);
        }
    }, [currentStepIndex, canGoNextValue, goToStep]);

    const previousStep = useCallback(() => {
        if (canGoPreviousValue) {
            goToStep(currentStepIndex - 1);
        }
    }, [currentStepIndex, canGoPreviousValue, goToStep]);

    const updateStepData = useCallback((stepKey, data) => {

        setFormData(prevData => {
            const updatedData = {
                ...prevData,
                [stepKey]: {
                    ...prevData[stepKey],
                    ...data
                }
            };

            return updatedData;
        });

        // Update Redux state based on step
        switch (stepKey) {
            case WIZARD_STEP_KEYS.BASIC_INFO:
                if (data.powertrainName !== undefined && actions?.setPowertrainNameAction) {
                    actions.setPowertrainNameAction(data.powertrainName);
                }
                if (data.selectedSite && actions?.setSelectedSiteAction) {
                    actions.setSelectedSiteAction(data.selectedSite);
                }
                break;
            case WIZARD_STEP_KEYS.ASSET_SELECTION:
                if (data.addedAssets) {
                    // Update added assets in Redux
                }
                break;
            default:
                break;
        }
    }, [actions]);

    const resetWizard = useCallback(() => {
        // Reset to first step through Redux action or local state
        if (actions?.setWizardActiveStep && wizardConfig?.steps?.[0]) {
            actions.setWizardActiveStep(wizardConfig.steps[0].id);
        } else {
            setLocalCurrentStepIndex(0);
        }
        setFormData({});
        if (actions?.clearStore) {
            actions.clearStore();
        }
    }, [actions, wizardConfig?.steps]);

    return {
        currentStep,
        currentStepIndex,
        formData,
        canGoNext: canGoNextValue,
        canGoPrevious: canGoPreviousValue,
        isLastStep,
        isFirstStep,
        goToStep,
        nextStep,
        previousStep,
        updateStepData,
        resetWizard
    };
};
